import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const KEY_SESSION = 'vs_session';
const DEV_EMAIL = 'admin@dev.local';
const DEV_PASS = 'admin';

interface LoginResponse {
  ok: boolean;
  email?: string;
  name?: string;
  role?: string;
  staffId?: string;
  error?: string;
}

interface RegisterResponse {
  ok: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = environment.apiUrl;

  constructor(private router: Router, private http: HttpClient) {}

  async login(email: string, password: string): Promise<boolean> {
    // Dev shortcut
    if (isDevMode() && email === DEV_EMAIL && password === DEV_PASS) {
      sessionStorage.setItem(KEY_SESSION, JSON.stringify({ email: DEV_EMAIL, name: 'Admin Dev', role: 'Directivo' }));
      return true;
    }

    try {
      const res = await firstValueFrom(
        this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, { email, password })
      );
      if (res.ok && res.email && res.name) {
        sessionStorage.setItem(KEY_SESSION, JSON.stringify({
          email: res.email,
          name: res.name,
          role: res.role ?? 'Directivo',
          staffId: res.staffId ?? null
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async register(name: string, email: string, password: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await firstValueFrom(
        this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register`, { name, email, password })
      );
      return res;
    } catch (err: any) {
      const error = err?.error?.error ?? 'unknown';
      return { ok: false, error };
    }
  }

  async resetPassword(email: string, newPassword: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/auth/reset-password`, { email, newPassword })
      );
      return true;
    } catch {
      return false;
    }
  }

  async changePassword(oldPwd: string, newPwd: string): Promise<boolean> {
    const session = this.getSession();
    if (!session) return false;

    try {
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/auth/change-password`, {
          email: session.email,
          oldPassword: oldPwd,
          newPassword: newPwd
        })
      );
      return true;
    } catch {
      return false;
    }
  }

  isLoggedIn(): boolean {
    return !!sessionStorage.getItem(KEY_SESSION);
  }

  getSession(): { email: string; name: string; role: string; staffId?: string | null } | null {
    try {
      return JSON.parse(sessionStorage.getItem(KEY_SESSION) || 'null');
    } catch {
      return null;
    }
  }

  getRole(): string {
    return this.getSession()?.role ?? 'Directivo';
  }

  /** Returns true if the current user can create/edit staff and students */
  canManage(): boolean {
    const role = this.getRole();
    return role === 'Directivo' || role === 'Administrativo';
  }

  /** Returns true if the current user can delete records */
  canDelete(): boolean {
    const role = this.getRole();
    return role === 'Directivo' || role === 'Administrativo';
  }

  /** Returns true if the current user can create/view interventions, actas, and informes */
  canContribute(): boolean {
    const role = this.getRole();
    return ['Directivo', 'Administrativo', 'Docente', 'Preceptor', 'Psicopedagogo', 'Auxiliar'].includes(role);
  }

  logout(): void {
    sessionStorage.removeItem(KEY_SESSION);
    this.router.navigate(['/login']);
  }
}
